import { OrganisationState, RootState, Organisation } from '@/types';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import OrganisationService from '@/services/organisation.service';
import router from '../../router';
import store from '../store';
import EventBus from '../../services/eventbus.service';
import ConnectivityService from '@/services/connectivity.service';

type OrganisationGetter = GetterTree<OrganisationState, RootState>;

export const initialState = (): OrganisationState => {
  return {
    initialState,
    organisations: [],
    activeOrganisationId: 0,
    hasTTISettings: false,
  };
};

export const getters: OrganisationGetter = {
  organisations: (state) => state,
  activeOrganisation: (state) => state.organisations.filter((item: Organisation) =>
    item.id === state.activeOrganisationId),
  activeOrganisationId: (state) => state.activeOrganisationId,
  hasTTISettings: (state) => state.hasTTISettings,
};

export const mutations: MutationTree<OrganisationState> = {
  addOrganisations(state, newOrganisations) {
    state.organisations = newOrganisations;
  },
  addOrganisation(state, newOrganisations) {
    state.organisations.push(newOrganisations);
  },
  updateOrganisation(state, updatedOrganisation: Organisation) {
    state.organisations[state.organisations.findIndex((project: Organisation) => project.id === updatedOrganisation.id)] = updatedOrganisation;
  },
  selectOrganisation(state, id) {
    state.activeOrganisationId = Number(id);
    // TODO: Review this in line with other state changes
    // store.commit('selectProject', 0);
  },
  setHasTTISettings(state, hasSettings) {
    state.hasTTISettings = hasSettings;
  },
};

export const actions: ActionTree<OrganisationState, RootState> = {
  async addOrganisationsAsync(context) {
    const orgs = await OrganisationService.getOrganisations();
    context.commit('addOrganisations', orgs);
    // Default to a selected organisation if not set
    let id: number | null | undefined = context.state.activeOrganisationId;
    if (!id || !context.getters.organisations.organisations.some((org: any) => org.id === id)) {
      id = orgs[0].id;
      await context.dispatch('changeOrganisationAsync', {orgId: id, forceRender: false});
    } else {
      await context.dispatch('updatePermissionsAsync', id);
    }
  },
  async createOrganisationAsync(context, details) {
    const organisation = await OrganisationService.createOrganisation(details);
    context.commit('addOrganisation', organisation);
    await context.dispatch('updatePermissionsAsync', organisation.id);
    return organisation;
  },
  async updateOrganisationAsync(context: any, project: { id: number, payload: object }) {
    const organisation = await OrganisationService.updateOrganisation(project.id, project.payload);
    context.commit('updateOrganisation', organisation);
    return organisation;
  },
  async changeOrganisationAsync(context: any, params: {orgId: number, forceRender: boolean}) {
    let orgId = params.orgId;
    if (store.getters.organisations.organisations.some((org: any) => org.id === store.getters.lastOrganisation)) {
      if (params.orgId !== store.getters.lastOrganisation) {
        orgId = store.getters.lastOrganisation;
      }
    }

    await context.dispatch('updatePermissionsAsync', orgId);
    context.commit('selectOrganisation', orgId);
    store.commit('selectProject', 0);
    if (store.getters.hasPermission('project:read')) {
      await store.dispatch('addProjectsAsync', {
        take: 0,
        skip: 0,
        sort: 'project.name',
        desc: false,
        filters: {},
      });
    }
    if (orgId !== Number(router.currentRoute.params.organisationId)) {
      EventBus.$emit('global-orgproj-change', {type: 'organisation', id: orgId, forceRender: params.forceRender});
    }
    const hasTTISettings = await ConnectivityService.hasTTISettings();
    context.commit('setHasTTISettings', hasTTISettings.result);
  },
};

export const organisations: Module<OrganisationState, RootState> = {
  state: initialState(),
  getters,
  mutations,
  actions,
};
